import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateServerTruthLists } from '../inbox-debug';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { bulkCloseTickets } from '../api';
import { useRefetchOnOpen } from '@/shared/hooks/useRefetchOnOpen';
import { useClosingReasons } from '../hooks';

interface Props {
  open: boolean;
  ticketIds: number[];
  /**
   * Algum dos selecionados é de canal que exige motivo? Ausente = exige.
   * Quando nenhum exige, encerra sem escolher e tudo entra no relatório como
   * "Não especificado".
   */
  exigeMotivo?: boolean;
  onClose: () => void;
  /** Chamado após sucesso total (failed.length === 0) — pra limpar seleção. */
  onSuccessAll: () => void;
  /** Ids que de fato encerraram (também no sucesso parcial) — a conversa
   *  aberta, se estiver entre eles, sai da tela igual ao encerrar 1. */
  onSucceeded?: (ticketIds: number[]) => void;
}

/**
 * Modal centralizado pra encerrar vários atendimentos ao mesmo tempo.
 * Mesmo padrão visual do CloseModal individual, mas com input de count e
 * tratamento de sucesso parcial via toast. Etapa única (pedido do dono,
 * ago/2026): escolheu o motivo → "Encerrar" executa direto, sem segunda
 * tela de confirmação.
 */
export function BulkCloseModal({
  open,
  ticketIds,
  exigeMotivo = true,
  onClose,
  onSuccessAll,
  onSucceeded,
}: Props) {
  const qc = useQueryClient();
  const reasonsQ = useClosingReasons();
  useRefetchOnOpen(open, reasonsQ.refetch);
  const [selected, setSelected] = useState<number | null>(null);

  const closeMut = useMutation({
    mutationFn: (closing_reason_id?: number) =>
      bulkCloseTickets({
        ticket_ids: ticketIds,
        ...(closing_reason_id !== undefined && { closing_reason_id }),
      }),
    onSuccess: (r) => {
      invalidateServerTruthLists(qc, 'bulkCloseMut');
      if (r.succeeded.length > 0) onSucceeded?.(r.succeeded);
      if (r.failed.length === 0) {
        toast.success(
          `${r.succeeded.length} ${r.succeeded.length === 1 ? 'atendimento encerrado' : 'atendimentos encerrados'} com sucesso`,
        );
        setSelected(null);
        onSuccessAll();
        onClose();
      } else {
        toast.warning(
          `${r.succeeded.length} encerrado${r.succeeded.length === 1 ? '' : 's'}, ${r.failed.length} falharam`,
        );
      }
    },
    onError: (err) => {
      toast.error('Erro ao encerrar atendimentos', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  // ESC fecha (listener no document — o onKeyDown do overlay só valia com foco).
  useEscapeClose(open, onClose);

  if (!open) return null;

  const submitting = closeMut.isPending;

  const handleClose = (): void => {
    if (submitting) return;
    setSelected(null);
    onClose();
  };

  const submit = (): void => {
    if ((exigeMotivo && selected === null) || submitting) return;
    closeMut.mutate(selected ?? undefined);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
      onKeyDown={(e) => e.key === 'Escape' && handleClose()}
    >
      <div
        className="w-[440px] max-w-[90vw] rounded-md border border-n-200 bg-n-0 p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <h2 className="font-bold text-[16px] text-n-900">Encerrar atendimento em massa</h2>
        <p className="mt-2 text-[13px] text-n-700">
          Você vai encerrar <strong>{ticketIds.length}</strong>{' '}
          {ticketIds.length === 1 ? 'atendimento' : 'atendimentos'}. Essa ação não poderá ser
          desfeita.
        </p>

        <div className="mt-4">
          <label htmlFor="bulk-close-reason" className="font-semibold text-[12px] text-n-700">
            Motivo
          </label>
          <select
            id="bulk-close-reason"
            value={selected ?? ''}
            onChange={(e) => setSelected(e.target.value ? Number(e.target.value) : null)}
            className="mt-1 w-full rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
          >
            <option value="">
              {exigeMotivo ? 'Selecione um motivo…' : 'Sem motivo específico'}
            </option>
            {(reasonsQ.data ?? [])
              // Motivo de sistema não entra na lista (vide CloseModal).
              .filter((r) => r.active && !r.systemKey)
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
          </select>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="rounded-md border border-n-300 bg-n-0 px-4 py-2 font-bold text-[12px] text-n-700 uppercase tracking-[0.05em] hover:bg-n-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={(exigeMotivo && selected === null) || submitting}
            onClick={submit}
            className="flex items-center gap-2 rounded-md bg-danger-500 px-4 py-2 font-bold text-[12px] text-white uppercase tracking-[0.05em] hover:bg-danger-600 disabled:opacity-50"
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {submitting
              ? 'Encerrando…'
              : `Encerrar ${ticketIds.length === 1 ? 'atendimento' : 'todos'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
